import { createContext } from 'react'
import type { AIStateStore } from '../../components/Mindmap/hooks/AIStateStore'
import type { AISuggestion } from '../../components/Mindmap/types/aiTypes'
import type { AIOperation, MindMapNode } from '@bandos/shared'

/**
 * Context for the AI state store (pub/sub pattern)
 *
 * This store holds the volatile state (sourceNodes, suggestions) and allows
 * components to subscribe to changes. Components use useAINodeState hook
 * to get per-node derived state with minimal re-renders.
 *
 * KEY INSIGHT: By separating the store from the callbacks context, we ensure
 * that callback identity changes don't cause store subscribers to re-render,
 * and vice versa.
 */
export const AIStateStoreContext = createContext<AIStateStore | null>(null)

/**
 * Context value for AI Generation callbacks
 *
 * IMPORTANT: This context ONLY contains stable callbacks.
 * State-based queries (isNodeGenerating, hasSuggestions, etc.) should use
 * the useAINodeState hook instead, which provides per-node subscriptions.
 *
 * Separating callbacks from state is the key optimization:
 * - Callbacks are wrapped in useCallback and rarely change
 * - State changes frequently but with per-node subscriptions, only affected nodes re-render
 */
export interface AICallbacksContextValue {
  // Dismiss all suggestions for a source node
  dismissSourceNode: (nodeId: string) => Promise<void>

  // Toggle a suggestion's selected state (adds/removes from mindmap)
  toggleSuggestion: (suggestionId: string) => Promise<void>

  // Get suggestions for a specific node
  // Note: For reactive updates, use useAISuggestionsForNode hook instead
  getSuggestionsForNode: (nodeId: string) => AISuggestion[]

  // Add chat-generated suggestions to canonical state (used by useNodeChat)
  addChatSuggestions: (
    nodeId: string,
    newSuggestions: AISuggestion[],
    sourceNodeUpdate: {
      node: MindMapNode
      operation: AIOperation
      feedbackEntry?: string
    }
  ) => void

  // Read feedback history for a source node (used by chat for API context)
  getFeedbackHistory: (nodeId: string) => string[] | undefined
}

export const AICallbacksContext = createContext<AICallbacksContextValue | null>(null)
